/**
 * Technician performance.
 *
 * Honest metrics: every number here is one the API actually computes, and the ones it does not
 * are named rather than approximated. The median time on site is a median and not a mean
 * because "one job where he forgot to tap completed until the next morning would drag an
 * average into fiction" — that is the API's own reasoning, in routes/dashboard.ts, and it is
 * the difference between a metric and a stick to beat a man with.
 *
 * No charts. Eight technicians and five columns is a table, and a table is comparable down the
 * column in a way a bar chart is not.
 */

import { useState } from "react";
import { formatIQD } from "@fanni/shared/money";
import { fmtDuration } from "../lib/format.js";
import { useTechnicianStats } from "../lib/queries.js";
import {
  Badge,
  Card,
  Empty,
  ErrorNote,
  Loading,
  Note,
  PageHeader,
  Select,
  Table,
  Td,
  Th,
  Tr,
} from "../ui/kit.js";

const RANGES = [
  { days: 7, label: "آخر ٧ أيام" },
  { days: 30, label: "آخر ٣٠ يوماً" },
  { days: 90, label: "آخر ٩٠ يوماً" },
];

export function Performance() {
  const [days, setDays] = useState(30);
  const { data, isLoading, error } = useTechnicianStats(days);

  const rows = [...(data?.technicians ?? [])].sort((a, b) => b.completed - a.completed);
  const perDay = (completed: number) => completed / days;

  return (
    <>
      <PageHeader
        title="أداء الفنيين"
        subtitle="ما يقوله الدفتر فعلاً. لا مؤشرات تجميلية."
        actions={
          <Select value={days} onChange={(e) => setDays(Number(e.target.value))} className="w-40">
            {RANGES.map((r) => (
              <option key={r.days} value={r.days}>
                {r.label}
              </option>
            ))}
          </Select>
        }
      />

      {error ? <ErrorNote error={error} className="mb-4" /> : null}

      <Card bodyClassName="">
        {isLoading ? (
          <Loading />
        ) : rows.length === 0 ? (
          <Empty>لا يوجد فنيون.</Empty>
        ) : (
          <Table>
            <thead>
              <tr>
                <Th>الفني</Th>
                <Th>مُسندة إليه</Th>
                <Th>أنجزها</Th>
                <Th>مهام/يوم</Th>
                <Th>وسيط زمن التنفيذ</Th>
                <Th>كلفة المواد</Th>
                <Th>أُلغيت</Th>
              </tr>
            </thead>
            <tbody>
              {rows.map((t) => (
                <Tr key={t.id}>
                  <Td className="font-medium">{t.name}</Td>
                  <Td>
                    <span className="num">{t.assigned}</span>
                  </Td>
                  <Td>
                    <span className="num font-bold text-good">{t.completed}</span>
                  </Td>
                  <Td>
                    <span className="num">{perDay(t.completed).toFixed(1)}</span>
                  </Td>
                  <Td>
                    <span className="num-rtl">{fmtDuration(t.median_minutes)}</span>
                  </Td>
                  <Td>
                    <span className="num">{t.material_display}</span>
                  </Td>
                  <Td>
                    {t.cancelled > 0 ? (
                      <Badge tone="warn">
                        <span className="num">{t.cancelled}</span>
                      </Badge>
                    ) : (
                      <span className="text-ink-faint">—</span>
                    )}
                  </Td>
                </Tr>
              ))}
              <tr>
                <Td className="bg-line-soft font-bold">الإجمالي</Td>
                <Td className="bg-line-soft">
                  <span className="num font-bold">{rows.reduce((a, t) => a + t.assigned, 0)}</span>
                </Td>
                <Td className="bg-line-soft">
                  <span className="num font-bold">{rows.reduce((a, t) => a + t.completed, 0)}</span>
                </Td>
                <Td className="bg-line-soft" />
                <Td className="bg-line-soft" />
                <Td className="bg-line-soft">
                  <span className="num font-bold">
                    {formatIQD(rows.reduce((a, t) => a + t.material_fils, 0))}
                  </span>
                </Td>
                <Td className="bg-line-soft">
                  <span className="num font-bold">{rows.reduce((a, t) => a + t.cancelled, 0)}</span>
                </Td>
              </tr>
            </tbody>
          </Table>
        )}
      </Card>

      <div className="mt-4 grid gap-4 lg:grid-cols-2">
        <Card title="كيف تُقرأ هذه الأرقام">
          <ul className="space-y-2 text-xs text-ink-soft">
            <li>
              <strong className="text-ink">وسيط زمن التنفيذ</strong> — من «بدأ العمل» إلى «أنجز»،
              بالوسيط لا بالمعدّل. مهمة واحدة نسي أن يقفلها حتى صباح اليوم التالي كانت ستفسد المعدّل
              تماماً.
            </li>
            <li>
              <strong className="text-ink">كلفة المواد</strong> — ما سجّله من مواد على مهامه. هذا
              جواب سؤالك الثاني: أين تذهب المواد.
            </li>
            <li>
              <strong className="text-ink">أُلغيت</strong> — رقم يُقرأ ولا يُدان به: الإلغاء قرار
              المكتب لا قرار الفني.
            </li>
          </ul>
        </Card>

        <Card title="مقاييس غير متاحة">
          <Note className="mb-2">
            <strong>الإيراد المتحقق لكل فني:</strong> لا يوجد منفذ يربط الفواتير بالفني.
            <span className="num"> /dashboard/technicians </span>
            يُرجِع كلفة المواد فقط، و<span className="num">/money/invoices</span> لا يحمل
            <span className="num"> technician_id</span>.
          </Note>
          <Note>
            <strong>الالتزام بالتصوير:</strong> لا يوجد منفذ يُرجِع عدد صور كل فني. الجدول
            <span className="num"> media </span>
            يحمل <span className="num">device_id</span> لا <span className="num">user_id</span>،
            والربط بالفني يحتاج ضمّاً عبر المهمة لا يوفّره أي منفذ اليوم.
          </Note>
        </Card>
      </div>
    </>
  );
}
